import {
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  configApiRef,
  type OAuthApi,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type BackstageIdentityApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import { type SignInPageProps } from '@backstage/core-app-api';
import {
  ApiBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';

// The new frontend system ships no built-in Auth0 ref, so define one
// bound to the backend provider id 'auth0'.
export const auth0AuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({ id: 'auth.auth0' });

const auth0ApiExtension = ApiBlueprint.make({
  name: 'auth0',
  params: define =>
    define(
      createApiFactory({
        api: auth0AuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            configApi,
            discoveryApi,
            oauthRequestApi,
            provider: { id: 'auth0', title: 'Auth0', icon: () => null },
            environment: configApi.getOptionalString('auth.environment'),
            // offline_access is required for Auth0 to issue a refresh token;
            // without it the session dies on every page reload.
            defaultScopes: ['openid', 'email', 'profile', 'offline_access'],
            // Ensure offline_access rides on every token request, not just the
            // initial sign-in (mirrors @backstage/core-app-api MicrosoftAuth).
            // OAuth2.normalizeScopes de-dupes via a Set, so concat is safe.
            scopeTransform: scopes => scopes.concat('offline_access'),
          }),
      }),
    ),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => (props: SignInPageProps) =>
      (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'auth0',
            title: 'Auth0',
            message: 'Sign in using your Autumn account',
            apiRef: auth0AuthApiRef,
          }}
        />
      ),
  },
});

export const auth0Module = createFrontendModule({
  pluginId: 'app',
  extensions: [auth0ApiExtension, signInPage],
});
